import axiosInstance from '../axios/axios-instance';
import { ContinueGameResponseDto } from 'shared/dto/game/continue-game-response.dto';
import { SignedGameTileDto } from 'shared/dto/game/signed-game-tile.dto';
import { StartGameResponseDto } from 'shared/dto/game/start-game-response.dto';
import { ApprovedGameGoals } from 'shared/interfaces/approved-game-goals/approved-game-goals.interface';
import { GameViewDetailsDto } from 'shared/interfaces/game/game-view-details.dto';
import { InProgressAndArchivedGameDetailsResponseDto } from 'shared/interfaces/game/in-progress-and-archived-game-details-response.dto';
import { InProgressGameDto } from 'shared/interfaces/game/in-progress-game.dto';
import { ViewGameRequestDto } from 'shared/interfaces/game/view-game-request.dto';
import { ContinueGameTestOptions } from 'src/api/game/continue-game-test-options.interface';

export const gameApi = {
  /**
   * Fetches the user's started and in-progress games for the "Continue your progress" widget.
   */
  async getInProgressGames(): Promise<InProgressGameDto[]> {
    const response = await axiosInstance.get<InProgressGameDto[]>('/game/in-progress');
    return response.data;
  },

  /**
   * Fetches the user's started, in-progress and archived games with goals and detail content.
   */
  async getInProgressAndArchivedGameDetails(): Promise<InProgressAndArchivedGameDetailsResponseDto> {
    const response = await axiosInstance.get<InProgressAndArchivedGameDetailsResponseDto>(
      '/game/in-progress-and-archived/details'
    );
    return response.data;
  },

  /**
   * Records a game view and returns display-safe game details for the game modal.
   */
  async viewGame(request: ViewGameRequestDto): Promise<GameViewDetailsDto> {
    const response = await axiosInstance.post<GameViewDetailsDto>('/game/view', request);
    return response.data;
  },

  /**
   * Starts a game session and returns the user-specific partner entry link.
   */
  async startGame(request: SignedGameTileDto, verisoulSessionId?: string): Promise<StartGameResponseDto> {
    const response = await axiosInstance.post<StartGameResponseDto>('/game/start', request, {
      headers: {
        'x-session-id': verisoulSessionId,
      },
    });
    return response.data;
  },

  /**
   * Continues (resumes) a started or in-progress game session and returns the resume redirect link when checks pass.
   */
  async continueGame(
    sessionUid: string,
    verisoulSessionId?: string,
    options?: ContinueGameTestOptions
  ): Promise<ContinueGameResponseDto> {
    const response = await axiosInstance.post<ContinueGameResponseDto>(
      '/game/continue',
      { sessionUid, ...options },
      {
        headers: {
          'x-session-id': verisoulSessionId,
        },
      }
    );
    return response.data;
  },

  /**
   * Loads the user's approved game goals as one combined modal for the current user.
   * @returns Combined approved game-goals modal, or null when there is nothing to show.
   */
  async getApprovedGameGoals(): Promise<ApprovedGameGoals | null> {
    const response = await axiosInstance.get<ApprovedGameGoals | null>('/game/goals/approved');
    return response.data;
  },

  /**
   * Pays the approved game-goal rewards as soon as the game-goals modal is visible.
   * @returns Paid game-goals progress payload with reward, level, quest, and streak deltas.
   */
  async payApprovedGameGoals(): Promise<ApprovedGameGoals | null> {
    const response = await axiosInstance.post<ApprovedGameGoals | null>('/game/goals/pay');

    return response.data;
  },
};
